import { MainLayout } from '@/components/Layout/MainLayout';
import { Button } from '@/components/ui/button';
import {
  Activity,
  BarChart3,
  Brain,
  Check,
  Database,
  Heart,
  Lock,
  Shield,
  Users,
  Zap,
  ArrowRight,
  CheckCircle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function Index() {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate('/login');
  };

  return (
    <MainLayout>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-primary/5 via-transparent to-transparent pt-20 md:pt-32 pb-20">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8 animate-fade-in">
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium">
                  <Zap className="h-4 w-4" />
                  Platformă de Inteligență de Risc Clinic
                </div>
                <h1 className="text-5xl md:text-6xl font-bold tracking-tight">
                  Detecția Timpurie a Riscului
                  <span className="bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
                    {' '}pentru Excelenţă Clinică
                  </span>
                </h1>
              </div>

              <p className="text-xl text-muted-foreground leading-relaxed">
                O platformă alimentată de AI care integrează scoruri clinice de risc validate cu date multimodale ale pacienților pentru a permite detecția timpurie și prevenția în mai multe specialități medicale din Europa Centrală și de Est.
              </p>

              <div className="space-y-3 text-sm text-muted-foreground">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0" />
                  <span>Sistem TRL 9 de calitate producție pentru centre medicale</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0" />
                  <span>Conform GDPR cu securitate avansată și criptare</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0" />
                  <span>Revizuirea clinicianului necesară pentru toate evaluările de risc</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button size="lg" onClick={handleGetStarted} className="gap-2">
                  Începeți
                  <ArrowRight className="h-4 w-4" />
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <a href="#features">Aflați mai multe</a>
                </Button>
              </div>
            </div>

            {/* Right Visual */}
            <div className="relative h-96 hidden lg:flex items-center justify-center">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-2xl blur-3xl"></div>
              <div className="relative grid grid-cols-2 gap-4 w-full">
                <div className="space-y-4">
                  <div className="bg-gradient-to-br from-primary to-primary/60 rounded-xl p-6 text-white shadow-lg">
                    <BarChart3 className="h-8 w-8 mb-4" />
                    <p className="text-sm font-semibold">Predicția Riscului</p>
                  </div>
                  <div className="bg-gradient-to-br from-accent to-accent/60 rounded-xl p-6 text-white shadow-lg">
                    <Lock className="h-8 w-8 mb-4" />
                    <p className="text-sm font-semibold">Securitate</p>
                  </div>
                </div>
                <div className="space-y-4 pt-8">
                  <div className="bg-gradient-to-br from-secondary to-secondary/60 rounded-xl p-6 text-white shadow-lg">
                    <Users className="h-8 w-8 mb-4" />
                    <p className="text-sm font-semibold">Multi-Rol</p>
                  </div>
                  <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-6 text-white shadow-lg">
                    <Brain className="h-8 w-8 mb-4" />
                    <p className="text-sm font-semibold">Gata pentru AI/ML</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section id="features" className="py-20 md:py-28 border-t border-border/40">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl md:text-4xl font-bold">Capacități ale Platformei</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Caracteristici cuprinzătoare concepute pentru excelenţă clinică și conformitate de reglementare
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature Cards */}
            {[
              {
                icon: Brain,
                title: 'Evaluare Multimodală a Riscului',
                description: 'Integrează scoruri clinice validate (PHQ-9, GAD-7, Framingham) cu observațiile pacienților pentru stratificare cuprinzătoare a riscului.',
                color: 'from-primary',
                iconClass: 'text-primary',
              },
              {
                icon: Activity,
                title: 'Indicele de Risc Timpuriu (ERI)',
                description: 'Algoritm proprietar combinând sănătatea mintală, sănătatea fizică și factorii sociali într-un singur scor de risc clinic.',
                color: 'from-secondary',
                iconClass: 'text-secondary',
              },
              {
                icon: BarChart3,
                title: 'IA Explicabilă',
                description: 'Atribuția bazată pe SHAP arată clinicienilor exact care factori au contribuit la fiecare evaluare de risc.',
                color: 'from-accent',
                iconClass: 'text-accent',
              },
              {
                icon: Users,
                title: 'Sistem Multi-Rol',
                description: 'Fluxuri de lucru adaptate pentru pacienți, clinicieni, manageri de date, administratori și analiști de cercetare cu permisiuni granulare.',
                color: 'from-amber-500',
                iconClass: 'text-amber-500',
              },
              {
                icon: Lock,
                title: 'Conform GDPR',
                description: 'Pseudonimizare avansată, criptare, gestionarea consimțământului și piste de audit pentru protecția datelor medicale.',
                color: 'from-rose-500',
                iconClass: 'text-rose-500',
              },
              {
                icon: Database,
                title: 'Integrare EHR',
                description: 'Conectorii HL7/FHIR permit importul fără probleme de date din sistemele spitalelor existente cu validare calității.',
                color: 'from-cyan-500',
                iconClass: 'text-cyan-500',
              },
            ].map((feature, idx) => (
              <div
                key={idx}
                className="clinical-card group hover:shadow-lg hover:border-primary/30 transition-all duration-300"
              >
                <div className={`inline-flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br ${feature.color}/20`}>
                  <feature.icon className={`h-6 w-6 ${feature.iconClass}`} />
                </div>
                <h3 className="mt-4 text-lg font-semibold">{feature.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Risk Levels Visualization */}
      <section className="py-20 md:py-28 border-t border-border/40">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl md:text-4xl font-bold">Stratificare Risc</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Clasificare pe patru niveluri cu recomandări clinice clare
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                level: 'Risc Scăzut',
                score: '0 - 25',
                colorClass: 'text-eri-low',
                borderClass: 'border-eri-low',
                bgColor: 'from-green-500/10',
                icon: '✓',
                actions: ['Îngrijire preventivă de rutină', 'Screening anual', 'Educație în sănătate'],
              },
              {
                level: 'Risc Moderat',
                score: '25 - 50',
                colorClass: 'text-eri-moderate',
                borderClass: 'border-eri-moderate',
                bgColor: 'from-amber-500/10',
                icon: '!',
                actions: ['Urmărire de rutină', 'Intervenții țintite', 'Modificare factori risc'],
              },
              {
                level: 'Risc Înalt',
                score: '50 - 75',
                colorClass: 'text-eri-high',
                borderClass: 'border-eri-high',
                bgColor: 'from-red-500/10',
                icon: '⚠',
                actions: ['Revizuire clinician urgent', 'Consult specialitate', 'Monitorizare apropiată'],
              },
              {
                level: 'Risc Critic',
                score: '75 - 100',
                colorClass: 'text-eri-critical',
                borderClass: 'border-eri-critical',
                bgColor: 'from-red-600/10',
                icon: '🚨',
                actions: ['Intervenție imediată', 'Gestionare criză', 'Spitalizare'],
              },
            ].map((risk, idx) => (
              <div key={idx} className={`clinical-card bg-gradient-to-b ${risk.bgColor} border-l-4 ${risk.borderClass}`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-lg font-semibold ${risk.colorClass}`}>{risk.level}</h3>
                  <div className={`text-3xl font-bold ${risk.colorClass}`}>{risk.icon}</div>
                </div>
                <p className="text-sm text-muted-foreground mb-4">Interval Scor: {risk.score}</p>
                <ul className="space-y-2">
                  {risk.actions.map((action, aIdx) => (
                    <li key={aIdx} className="flex items-start gap-2 text-xs text-muted-foreground">
                      <Check className={`h-4 w-4 ${risk.colorClass} flex-shrink-0 mt-0.5`} />
                      <span>{action}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* User Roles Section */}
      <section id="roles" className="py-20 md:py-28 bg-gradient-to-b from-muted/50 to-transparent border-t border-border/40">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl md:text-4xl font-bold">Roluri și Fluxuri de Lucru ale Utilizatorului</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Interfețe și permisiuni adaptate pentru fiecare parte interesată
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                role: 'Pacient',
                icon: Heart,
                color: 'from-rose-500',
                iconClass: 'text-rose-500',
                workflows: ['Completați chestionare de sănătate', 'Vizualizați rezumat risc personal', 'Gestionați preferințe consimțământ'],
              },
              {
                role: 'Clinician (MD/RN)',
                icon: Activity,
                color: 'from-blue-500',
                iconClass: 'text-blue-500',
                workflows: ['Revizuiți scoruri risc pacient', 'Adăugați observații clinice', 'Validați rezultate ERI'],
              },
              {
                role: 'Manager Date',
                icon: Database,
                color: 'from-cyan-500',
                iconClass: 'text-cyan-500',
                workflows: ['Importați date EHR', 'Validați calitate date', 'Monitorizați conductele date'],
              },
              {
                role: 'Analist Cercetare',
                icon: BarChart3,
                color: 'from-purple-500',
                iconClass: 'text-purple-500',
                workflows: ['Interogați cohorte de-identificate', 'Exportați seturi date cercetare', 'Rulați analize echitate'],
              },
              {
                role: 'Admin Platformă',
                icon: Shield,
                color: 'from-amber-500',
                iconClass: 'text-amber-500',
                workflows: ['Gestionați utilizatori și roluri', 'Configurați permisiuni', 'Revizuiți jurnale audit'],
              },
              {
                role: 'Operator Platformă',
                icon: Zap,
                color: 'from-green-500',
                iconClass: 'text-green-500',
                workflows: ['Monitorizați sănătate sistem', 'Implementați actualizări', 'Urmăriți metrici SLA'],
              },
            ].map((user, idx) => (
              <div key={idx} className="clinical-card">
                <div className={`inline-flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br ${user.color}/20`}>
                  <user.icon className={`h-6 w-6 ${user.iconClass}`} />
                </div>
                <h3 className="mt-4 text-lg font-semibold">{user.role}</h3>
                <ul className="mt-4 space-y-2">
                  {user.workflows.map((workflow, wIdx) => (
                    <li key={wIdx} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check className="h-4 w-4 text-secondary flex-shrink-0 mt-0.5" />
                      <span>{workflow}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance & Security */}
      <section id="compliance" className="py-20 md:py-28 border-t border-border/40">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl md:text-4xl font-bold">Conformitate și Securitate</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Protecție de nivel întreprindere pentru date medicale sensibile
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Security Features */}
            <div className="space-y-6">
              <h3 className="text-2xl font-semibold">Caracteristici Securitate</h3>
              <div className="space-y-4">
                {[
                  { title: 'Criptare Stare Repaos', desc: 'AES-256 pentru date PII, integrare PostgreSQL pgcrypto' },
                  { title: 'Criptare în Tranzit', desc: 'TLS 1.3 pentru toate comunicațiile API' },
                  { title: 'Autentificare', desc: 'OAuth2/OIDC cu Autentificare Multi-Factor (MFA)' },
                  { title: 'Autorizare', desc: 'Control Acces Bazat pe Rol (RBAC) cu securitate nivel rând' },
                ].map((feature, idx) => (
                  <div key={idx} className="flex gap-4">
                    <Lock className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-sm">{feature.title}</p>
                      <p className="text-sm text-muted-foreground mt-1">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Compliance Standards */}
            <div className="space-y-6">
              <h3 className="text-2xl font-semibold">Standarde Conformitate</h3>
              <div className="space-y-4">
                {[
                  { title: 'GDPR', desc: 'Protecție date, confidențialitate, gestionare consimțământ, dreptul de a fi uitat' },
                  { title: 'ISO 27001', desc: 'Certificare sistem gestionare securitate informații' },
                  { title: 'Pseudonimizare Date', desc: 'Identificare pacient bazată pe UUID cu criptare' },
                  { title: 'Retenție Date', desc: 'Politici retenție configurabile cu ștergere automată' },
                ].map((feature, idx) => (
                  <div key={idx} className="flex gap-4">
                    <Shield className="h-5 w-5 text-secondary flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-sm">{feature.title}</p>
                      <p className="text-sm text-muted-foreground mt-1">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-gradient-to-r from-primary/10 via-secondary/5 to-accent/10 border-t border-border/40">
        <div className="container max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <div className="space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold">Gata să Transformați Detecția Riscului Clinic?</h2>
            <p className="text-lg text-muted-foreground">
              Alătură-vă centrelor medicale de vârf din Europa Centrală și de Est care folosesc EARLYMED AI pentru detecția timpurie a riscului și îngrijire predictivă.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={handleGetStarted}>
              Accesați Platforma
            </Button>
            <Button size="lg" variant="outline" asChild>
              <a href="#features">Aflați mai multe</a>
            </Button>
          </div>

          <p className="text-sm text-muted-foreground">
            Platformă securizată pentru profesioniștii medicali calificați.
          </p>
        </div>
      </section>
    </MainLayout>
  );
}
